import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import PrintIcon from '@mui/icons-material/Print';

/**
 * Receipt dialog shown after a successful checkout.
 *
 * Shows invoice number (or offline queue ID), items, payments, totals.
 * Print button uses window.print() for basic printing.
 */
export default function ReceiptDialog({ open, onClose, receiptData }) {
  if (!receiptData) return null;

  const { mode, invoice, queuedId, items, payments, subtotal, change } = receiptData;
  const isOffline = mode === 'offline';
  const invoiceName = invoice?.name || queuedId || '—';
  const grandTotal = invoice?.grand_total || subtotal;
  const timestamp = new Date().toLocaleString();

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        {isOffline ? (
          <CloudOffIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
        ) : (
          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
        )}
        <Typography variant="h6" component="div">
          {isOffline ? 'Sale Queued' : 'Sale Complete'}
        </Typography>
        <Chip
          label={isOffline ? 'Pending Sync' : 'Submitted'}
          color={isOffline ? 'warning' : 'success'}
          size="small"
          sx={{ mt: 0.5 }}
        />
      </DialogTitle>

      <DialogContent>
        {/* Invoice info */}
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {isOffline ? 'Queue ID' : 'Invoice'}
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {invoiceName}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">Date/Time</Typography>
            <Typography variant="caption">{timestamp}</Typography>
          </Stack>
        </Stack>

        <Divider />

        {/* Items */}
        <Table size="small" sx={{ my: 1 }}>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.item_code} sx={{ '& td': { borderBottom: 'none', py: 0.3 } }}>
                <TableCell sx={{ pl: 0 }}>
                  <Typography variant="body2">{item.item_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.rate.toFixed(2)} x {item.qty}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ pr: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {item.amount.toFixed(2)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider />

        {/* Totals */}
        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2">{subtotal.toFixed(2)}</Typography>
          </Stack>
          {invoice?.total_taxes_and_charges > 0 && (
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Tax</Typography>
              <Typography variant="body2">
                {invoice.total_taxes_and_charges.toFixed(2)}
              </Typography>
            </Stack>
          )}
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={700}>Grand Total</Typography>
            <Typography variant="subtitle1" fontWeight={700}>
              {grandTotal.toFixed(2)}
            </Typography>
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Payments */}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Payments
        </Typography>
        <Stack spacing={0.3}>
          {payments.map((p, i) => (
            <Stack key={i} direction="row" justifyContent="space-between">
              <Typography variant="body2">{p.mode_of_payment}</Typography>
              <Typography variant="body2">{p.amount.toFixed(2)}</Typography>
            </Stack>
          ))}
          {change > 0 && (
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="success.main">Change</Typography>
              <Typography variant="body2" color="success.main" fontWeight={600}>
                {change.toFixed(2)}
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button startIcon={<PrintIcon />} onClick={handlePrint}>
          Print
        </Button>
        <Button variant="contained" onClick={onClose}>
          New Sale
        </Button>
      </DialogActions>
    </Dialog>
  );
}
